/**
 * ExecPreReadPanel — Phase 4 frontend for the organizer-only exec brief.
 *
 * Production-grade properties:
 *
 *   - Renders nothing for non-organizers, ineligible meetings, or child
 *     occurrences (the eligibility check returns eligible=false with a
 *     specific reason; we honor it and return null so the UI stays clean).
 *
 *   - Polls /status every 2s only while state is non-terminal. Cleans up
 *     the interval on unmount.
 *
 *   - Download uses fetch+blob so the Bearer token can be sent in headers
 *     (a plain <a href> can't carry custom headers).
 *
 *   - Rate-limit (429) is handled with a clear toast message.
 *
 *   - Recurring parent vs one-off vs child occurrence labels adapt.
 */

import { useEffect, useRef, useState } from 'react';
import { preReadAPI } from '../services/api';
import { FileDown, RotateCcw, Loader2, AlertCircle, Sparkles, Eye, X } from 'lucide-react';

const TERMINAL_STATES = new Set(['idle', 'ready', 'failed']);
const POLL_INTERVAL_MS = 2000;

const STATE_LABELS = {
  idle: 'Ready to generate',
  checking_learning: 'Checking prior context',
  running_learning_for_prior: 'Learning from your last meeting',
  generating: 'Composing your exec brief',
  ready: 'Brief ready',
  failed: 'Generation failed',
};

const STATE_SUBLINES = {
  checking_learning: 'Looking at the last completed occurrence and confirming context is fresh.',
  running_learning_for_prior:
    'The most recent occurrence finished but learning hasn\'t completed yet. We\'re running it now.',
  generating: 'Rithmo is composing the brief from your spine, enrichments, and cross-meeting intelligence.',
};

export default function ExecPreReadPanel({ meetingId, meetingTitle }) {
  const [eligibility, setEligibility] = useState(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [opening, setOpening] = useState(false);
  const [viewerHtml, setViewerHtml] = useState(null);
  const pollRef = useRef(null);

  // Initial eligibility + status load.
  useEffect(() => {
    if (!meetingId) return;
    let cancelled = false;
    (async () => {
      try {
        const elig = await preReadAPI.getEligibility(meetingId);
        if (cancelled) return;
        setEligibility(elig?.data ?? elig);
      } catch (err) {
        if (!cancelled) setEligibility({ eligible: false, reason: 'fetch_failed', message: 'Could not check pre-read availability.' });
      }
      try {
        const st = await preReadAPI.getStatus(meetingId);
        if (cancelled) return;
        setStatus(st?.data ?? st);
      } catch {
        // Non-organizer or no state yet — that's fine.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [meetingId]);

  // Poll status while non-terminal.
  useEffect(() => {
    if (!status || TERMINAL_STATES.has(status.state)) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const st = await preReadAPI.getStatus(meetingId);
        setStatus(st?.data ?? st);
      } catch {
        /* keep polling */
      }
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [status?.state, meetingId]);

  // Escape closes the inline viewer.
  useEffect(() => {
    if (!viewerHtml) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setViewerHtml(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewerHtml]);

  if (!eligibility) return null;
  if (!eligibility.eligible) {
    // Child occurrence: tiny pointer to the parent page.
    if (eligibility.reason === 'is_child_occurrence') {
      return (
        <div className="text-xs text-slate-500 italic px-1 py-2">
          Exec pre-read for this series is generated from the parent meeting page.
        </div>
      );
    }
    // Persist the Exec Pre-Read element on recurring meetings even when we
    // can't generate yet — UI shouldn't appear/disappear on hidden logic. We
    // show the organizer exactly why (e.g. the series hasn't run its first
    // occurrence yet). For one-offs and access-gated cases (non-organizer,
    // rollout off — no meetingSnapshot) we still render nothing.
    if (eligibility.meetingSnapshot?.isRecurringParent === true) {
      return (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <Sparkles className="w-5 h-5 text-slate-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-700">
                Exec Pre-Read
                <span className="ml-2 text-xs font-normal text-slate-500">(organizer only)</span>
              </div>
              <div className="text-xs text-slate-600 mt-1">
                {eligibility.message ?? 'Not enough data to generate a pre-read yet.'}
              </div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  const isRecurringParent = eligibility.meetingSnapshot?.isRecurringParent === true;
  const buttonLabel = isRecurringParent
    ? `Generate Pre-Read for Next ${truncateTitle(meetingTitle)}`
    : 'Generate Exec Pre-Read';

  const handleGenerate = async (force = false) => {
    setBusy(true);
    setActionError(null);
    try {
      const resp = await preReadAPI.generate(meetingId, { force });
      const data = resp?.data ?? resp;
      setStatus({
        state: data?.state ?? 'generating',
        progress: 5,
        canDownload: false,
        atAGlance: null,
        errorReason: null,
        attemptCount: status?.attemptCount ?? 0,
      });
    } catch (err) {
      const msg = err?.message ?? 'Failed to start generation';
      const retryAfterSec = err?.retryAfterSec;
      if (retryAfterSec) {
        const min = Math.max(1, Math.ceil(retryAfterSec / 60));
        setActionError(`Please wait ${min} minute${min > 1 ? 's' : ''} before regenerating.`);
      } else {
        setActionError(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    setActionError(null);
    try {
      const token = localStorage.getItem('rithmo_token') ?? '';
      const resp = await fetch(preReadAPI.downloadUrl(meetingId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`Download failed (${resp.status})`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(meetingTitle ?? 'meeting').replace(/[^a-zA-Z0-9 \-_]/g, '')} - Exec Brief.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setActionError(err?.message ?? 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  // Open the brief inline (over the dashboard) in our own reader. We fetch the
  // server-rendered HTML (not the PDF) and drop it into a sandboxed <iframe
  // srcDoc> so it renders as a native, responsive document — no browser PDF
  // chrome. Legacy briefs (generated before output was persisted) return 409
  // needs_regenerate; we surface a clear prompt to regenerate.
  const handleOpen = async () => {
    setOpening(true);
    setActionError(null);
    try {
      const token = localStorage.getItem('rithmo_token') ?? '';
      const resp = await fetch(preReadAPI.viewUrl(meetingId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.status === 409) {
        let code = null;
        try {
          code = (await resp.json())?.code ?? null;
        } catch { /* ignore */ }
        if (code === 'needs_regenerate') {
          setActionError('This brief predates the new reader. Click Regenerate to view it here, or use Download for the PDF.');
          return;
        }
      }
      if (!resp.ok) throw new Error(`Could not open the brief (${resp.status})`);
      const html = await resp.text();
      setViewerHtml(html);
    } catch (err) {
      setActionError(err?.message ?? 'Could not open the brief');
    } finally {
      setOpening(false);
    }
  };

  const closeViewer = () => setViewerHtml(null);

  const state = status?.state ?? 'idle';

  return (
    <>
    <div className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-3">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          {state === 'ready' ? (
            <Sparkles className="w-5 h-5 text-violet-600" />
          ) : state === 'failed' ? (
            <AlertCircle className="w-5 h-5 text-rose-600" />
          ) : !TERMINAL_STATES.has(state) ? (
            <Loader2 className="w-5 h-5 text-violet-600 animate-spin" />
          ) : (
            <Sparkles className="w-5 h-5 text-violet-600" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-violet-900">
            Exec Pre-Read
            <span className="ml-2 text-xs font-normal text-violet-600">(organizer only)</span>
          </div>
          <div className="text-xs text-violet-700 mt-0.5">
            {STATE_LABELS[state] ?? state}
          </div>
          {STATE_SUBLINES[state] && (
            <div className="text-xs text-slate-600 mt-1.5">{STATE_SUBLINES[state]}</div>
          )}
          {state === 'ready' && status?.atAGlance && (
            <AtAGlance summary={status.atAGlance} />
          )}
          {state === 'failed' && status?.errorReason && (
            <div className="text-xs text-rose-700 mt-1.5">
              <span className="font-medium">Reason:</span> {status.errorReason}
            </div>
          )}
          {actionError && (
            <div className="text-xs text-rose-700 mt-1.5">{actionError}</div>
          )}
        </div>
        <div className="flex flex-col gap-1.5 flex-shrink-0">
          {state === 'idle' && (
            <button
              type="button"
              disabled={busy}
              onClick={() => handleGenerate(false)}
              className="inline-flex items-center gap-1.5 rounded-md bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-700 disabled:opacity-50"
            >
              <Sparkles className="w-3.5 h-3.5" />
              {buttonLabel}
            </button>
          )}
          {state === 'ready' && (
            <>
              <button
                type="button"
                disabled={opening}
                onClick={handleOpen}
                className="inline-flex items-center gap-1.5 rounded-md bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-700 disabled:opacity-50"
              >
                <Eye className="w-3.5 h-3.5" />
                {opening ? 'Opening…' : 'Open'}
              </button>
              <button
                type="button"
                disabled={downloading}
                onClick={handleDownload}
                className="inline-flex items-center gap-1.5 rounded-md border border-violet-300 bg-white px-3 py-1.5 text-xs font-medium text-violet-700 hover:bg-violet-50 disabled:opacity-50"
              >
                <FileDown className="w-3.5 h-3.5" />
                {downloading ? 'Downloading…' : 'Download'}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => handleGenerate(true)}
                className="inline-flex items-center gap-1.5 rounded-md border border-violet-300 bg-white px-3 py-1.5 text-xs font-medium text-violet-700 hover:bg-violet-50 disabled:opacity-50"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Regenerate
              </button>
            </>
          )}
          {state === 'failed' && (
            <button
              type="button"
              disabled={busy}
              onClick={() => handleGenerate(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-700 disabled:opacity-50"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    </div>

    {viewerHtml && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 sm:p-6"
        onClick={closeViewer}
        role="dialog"
        aria-modal="true"
        aria-label="Exec Pre-Read"
      >
        <div
          className="relative flex h-[94vh] w-full max-w-[940px] flex-col overflow-hidden rounded-2xl bg-slate-100 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-slate-200 bg-white px-5 py-3">
            <div className="flex items-center gap-2 min-w-0">
              <Sparkles className="h-4 w-4 flex-shrink-0 text-violet-600" />
              <div className="truncate text-sm font-semibold text-slate-800">
                Exec Pre-Read{meetingTitle ? ` — ${meetingTitle}` : ''}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={handleDownload}
                disabled={downloading}
                className="hidden sm:inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              >
                <FileDown className="h-3.5 w-3.5" />
                {downloading ? 'Downloading…' : 'Download PDF'}
              </button>
              <button
                type="button"
                onClick={closeViewer}
                aria-label="Close"
                className="flex-shrink-0 rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          <iframe
            title="Exec Pre-Read"
            srcDoc={viewerHtml}
            sandbox=""
            className="h-full w-full flex-1 border-0 bg-slate-100"
          />
        </div>
      </div>
    )}
    </>
  );
}

function AtAGlance({ summary }) {
  const items = [];
  if (summary.agendaItemCount != null) {
    items.push(`${summary.agendaItemCount} agenda items${summary.totalDurationMin ? ` · ${summary.totalDurationMin} min total` : ''}`);
  }
  if (summary.carryoverCount > 0) items.push(`${summary.carryoverCount} carryover items from last cycle`);
  if (summary.crossMeetingItemCount > 0) items.push(`${summary.crossMeetingItemCount} cross-meeting bullets`);
  if (summary.decisionPointsCount > 0) items.push(`${summary.decisionPointsCount} decision points`);
  if (summary.steeringNotesCount > 0) items.push(`${summary.steeringNotesCount} steering notes`);
  if (items.length === 0) return null;
  return (
    <ul className="text-xs text-slate-700 mt-2 list-disc pl-5 space-y-0.5">
      {items.map((t, i) => (
        <li key={i}>{t}</li>
      ))}
      {summary.isColdStart && (
        <li className="italic text-amber-700">Limited data — first-time meeting between these participants.</li>
      )}
    </ul>
  );
}

function truncateTitle(s) {
  if (!s) return 'Meeting';
  const trimmed = s.trim();
  if (trimmed.length <= 24) return trimmed;
  return trimmed.slice(0, 21) + '…';
}
